/**
 * Caramelo em movimento da "Nossa história" (<e-story-flow>).
 *
 * A foto sem o fio (primeiro quadro do vídeo) fica parada até a seção se
 * aproximar; aí o vídeo baixa. Com metade da mídia na tela, ele toca uma vez
 * (o fio desce e pousa no caramelo) e para no último quadro, sem loop. Fora da
 * tela ou com a aba escondida, pausa e retoma ao voltar. São 5 s: a WCAG 2.2.2
 * só pede botão de pausa para movimento acima de 5 s.
 *
 * Com movimento reduzido, economia de dados ou vídeo que não toca (autoplay
 * bloqueado no modo de pouca bateria, arquivo com erro), fica a foto com o fio
 * e o download em curso é cancelado.
 */

const START_RATIO = 0.5;

class EStoryFlow extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('[data-e-story-video]');
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!this.video || this.motionQuery.matches || navigator.connection?.saveData === true) {
      this.showStill();
      return;
    }

    this.onMotionChange = () => { if (this.motionQuery.matches) this.showStill(); };
    this.motionQuery.addEventListener('change', this.onMotionChange);
    this.onVisibility = () => this.sync();
    document.addEventListener('visibilitychange', this.onVisibility);
    this.onEnded = () => this.finish();
    this.video.addEventListener('ended', this.onEnded);
    this.observe();
  }

  disconnectedCallback() {
    this.motionQuery?.removeEventListener('change', this.onMotionChange);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.video?.removeEventListener('ended', this.onEnded);
    this.stopObserving();
  }

  observe() {
    this.nearObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.nearObserver.disconnect();
      this.load();
    }, { rootMargin: '75% 0px' });
    this.nearObserver.observe(this);

    this.viewObserver = new IntersectionObserver(([entry]) => {
      this.inView = entry.intersectionRatio >= START_RATIO || (this.started && entry.isIntersecting);
      this.sync();
    }, { threshold: [0, START_RATIO] });
    this.viewObserver.observe(this);
  }

  stopObserving() {
    this.nearObserver?.disconnect();
    this.viewObserver?.disconnect();
  }

  load() {
    if (this.video.dataset.loaded) return;
    this.video.dataset.loaded = 'true';
    this.video.querySelectorAll('source[data-src]').forEach((source) => { source.src = source.dataset.src; });
    this.video.preload = 'auto';
    this.video.load();
  }

  sync() {
    if (this.dataset.state === 'still' || this.dataset.state === 'done') return;
    if (!this.inView || document.visibilityState !== 'visible') {
      this.video.pause();
      return;
    }
    this.started = true;
    this.dataset.state = 'playing';
    // A página pode abrir já na seção (/#nossa-historia): aí este observer
    // chega antes do de pré-carga, e um load() depois do play() o abortaria.
    this.load();
    // AbortError = pausa no meio do play(), segue normal; qualquer outra falha vira foto.
    this.video.play().catch((error) => { if (error.name !== 'AbortError') this.showStill(); });
  }

  /** O vídeo fica no último quadro (o fio pousado no caramelo). */
  finish() {
    this.dataset.state = 'done';
    this.stopObserving();
  }

  /**
   * Foto com o fio no lugar do vídeo: some o <source> da foto sem o fio. Tirar
   * o src e chamar load() cancela o download que já tinha começado.
   */
  showStill() {
    this.dataset.state = 'still';
    this.stopObserving();
    if (this.video) {
      this.video.pause();
      if (this.video.dataset.loaded) {
        this.video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
        this.video.load();
      }
    }
    this.querySelector('[data-e-story-motion-still]')?.remove();
  }
}

if (!customElements.get('e-story-flow')) customElements.define('e-story-flow', EStoryFlow);
